import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, XCircle, Clock, Search, ChevronLeft, ChevronRight, Activity, ArrowUpDown, Filter } from "lucide-react";
import { formatJamaicaDateTime as formatDateTime } from "@/lib/datetime";

interface ApiLog {
  id: string;
  endpoint: string;
  method: string;
  summary: string;
  statusCode: number | null;
  responseTimeMs: number | null;
  errorMessage: string | null;
  success: boolean;
  createdAt: string;
}

interface LogsResponse {
  logs: ApiLog[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
}

function MethodBadge({ method }: { method: string }) {
  const variant = method === "GET" ? "secondary" : "default";
  return <Badge variant={variant}>{method}</Badge>;
}

function StatusIndicator({ success, statusCode }: { success: boolean; statusCode: number | null }) {
  if (success) {
    return (
      <div className="flex items-center gap-1.5">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <span className="text-sm text-green-600 dark:text-green-400">{statusCode || "OK"}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <XCircle className="h-4 w-4 text-red-500" />
      <span className="text-sm text-red-600 dark:text-red-400">{statusCode || "ERR"}</span>
    </div>
  );
}

function ResponseTime({ ms }: { ms: number | null }) {
  if (ms == null) return <span className="text-sm text-muted-foreground">--</span>;
  const color = ms < 500 ? "text-green-600 dark:text-green-400" : ms < 2000 ? "text-yellow-600 dark:text-yellow-400" : "text-red-600 dark:text-red-400";
  return (
    <div className="flex items-center gap-1">
      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
      <span className={`text-sm ${color}`}>{ms}ms</span>
    </div>
  );
}

export default function QuizzativeApiLogs() {
  const [page, setPage] = useState(1);
  const [methodFilter, setMethodFilter] = useState<string>("all");
  const [successFilter, setSuccessFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const buildUrl = () => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("per_page", "30");
    if (methodFilter !== "all") params.set("method", methodFilter);
    if (successFilter !== "all") params.set("success", successFilter);
    if (appliedSearch) params.set("search", appliedSearch);
    return `/api/admin/quizzative/api-logs?${params.toString()}`;
  };

  const { data, isLoading } = useQuery<LogsResponse>({
    queryKey: ["/api/admin/quizzative/api-logs", page, methodFilter, successFilter, appliedSearch],
    queryFn: async () => {
      const res = await fetch(buildUrl());
      if (!res.ok) throw new Error("Failed to fetch logs");
      return res.json();
    },
    refetchInterval: 15000,
  });

  const logs = data?.logs || [];
  const pagination = data?.pagination;

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setPage(1);
  };

  const successCount = logs.filter(l => l.success).length;
  const failCount = logs.filter(l => !l.success).length;
  const avgResponseTime = logs.length > 0
    ? Math.round(logs.filter(l => l.responseTimeMs != null).reduce((sum, l) => sum + (l.responseTimeMs || 0), 0) / Math.max(logs.filter(l => l.responseTimeMs != null).length, 1))
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Quizzative API Logs</h2>
        <p className="text-muted-foreground">Monitor all HTTP requests between EduFocal and Quizzative</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm text-muted-foreground">Total Calls</p>
                <p className="text-2xl font-bold">{pagination?.total ?? 0}</p>
              </div>
              <Activity className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm text-muted-foreground">This Page</p>
                <p className="text-2xl font-bold">
                  <span className="text-green-600 dark:text-green-400">{successCount}</span>
                  {failCount > 0 && <span className="text-red-600 dark:text-red-400"> / {failCount} failed</span>}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm text-muted-foreground">Avg Response</p>
                <p className="text-2xl font-bold">{avgResponseTime}ms</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-muted-foreground mb-1 block">Search summaries</label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. Grade 6, results..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                <Button size="icon" variant="outline" onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Method</label>
              <Select value={methodFilter} onValueChange={(v) => { setMethodFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Status</label>
              <Select value={successFilter} onValueChange={(v) => { setSuccessFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Success</SelectItem>
                  <SelectItem value="false">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No API calls logged yet. Logs will appear here after sync, result dispatch, or report fetches.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Time</th>
                    <th className="text-left p-3 font-medium">Method</th>
                    <th className="text-left p-3 font-medium">Endpoint</th>
                    <th className="text-left p-3 font-medium">Summary</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Response</th>
                    <th className="text-left p-3 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className={`border-b ${!log.success ? "bg-red-50/50 dark:bg-red-950/20" : ""}`}>
                      <td className="p-3 whitespace-nowrap text-muted-foreground">
                        {formatDateTime(log.createdAt)}
                      </td>
                      <td className="p-3">
                        <MethodBadge method={log.method} />
                      </td>
                      <td className="p-3 font-mono text-xs max-w-[200px] truncate" title={log.endpoint}>
                        {log.endpoint}
                      </td>
                      <td className="p-3 max-w-[300px]">
                        <span className="line-clamp-2">{log.summary}</span>
                      </td>
                      <td className="p-3">
                        <StatusIndicator success={log.success} statusCode={log.statusCode} />
                      </td>
                      <td className="p-3">
                        <ResponseTime ms={log.responseTimeMs} />
                      </td>
                      <td className="p-3 max-w-[200px]">
                        {log.errorMessage ? (
                          <span className="text-xs text-red-600 dark:text-red-400 line-clamp-2" title={log.errorMessage}>
                            {log.errorMessage}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">--</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between gap-2 p-4 border-t">
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
